import { ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { VehicleMakeDto, VehicleMakeGetPaginatedDto } from "@/core/api/generated/v1";

type VehicleMakeAutocompleteOption = BaseAutocompleteOption<VehicleMakeDto>;

const entityToOption = (data: VehicleMakeDto): VehicleMakeAutocompleteOption => ({
  id: data.id!,
  title: StringHelper.join([data.name], ", ", {
    skipEmpty: true,
  }),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface VehicleMakeAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<VehicleMakeDto> {
  searchFilters?: Partial<Omit<VehicleMakeGetPaginatedDto, "search" | "includeIds">>;
}

export default function VehicleMakeAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: VehicleMakeAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={entityToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.vehicleMakesApi.apiV1ReferenceDataVehiclesMakesGetPost,
        limit: 25,
        parameters: {
          vehicleMakeGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          vehicleMakeGetPaginatedDto: {
            ...params.vehicleMakeGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Make'
      placeholder='Search for make...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='vehicleMake' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
